import React from 'react';
const { useRef, useEffect, useMemo } = React;
import { BRAND_FORMATS } from '../../../brandConfig.js';
import { layoutPiece, drawPiece, drawOverlay, describeLayout } from '../../../brandRender.js';
import { measure } from './brandClient.js';

// The live preview of a piece: the same layout and drawing as the PNG export,
// at the export size, shown scaled. The safe-zone overlay is drawn on top only
// here (never exported). The canvas is an image for screen readers: its
// aria-label says what it shows.

export function usePieceLayout(piece, { brand, images, when, slide }) {
  return useMemo(() => (piece && brand ? layoutPiece(piece, { brand, measure, images, when, slide }) : null),
    [piece, brand, images, when, slide]);
}

export default function PiecePreview({ piece, brand, images = {}, when = null, slide = 0, overlay = false, maxWidth = 420, layout: given = null, label = 'Vista previa' }) {
  const ref = useRef(null);
  const own = usePieceLayout(given ? null : piece, { brand, images, when, slide });
  const layout = given || own;
  const fmt = BRAND_FORMATS[piece?.format];
  useEffect(() => {
    const c = ref.current;
    if (!c || !layout || !fmt) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    drawPiece(ctx, layout, images);
    if (overlay) drawOverlay(ctx, layout, piece.format);
  }, [layout, overlay, images, fmt, piece?.format]);
  if (!fmt || !layout) return null;
  return (
    <figure style={{ margin: 0, width: '100%', maxWidth }}>
      <canvas ref={ref} width={fmt.width} height={fmt.height} role="img" aria-label={`${label}: ${describeLayout(layout) || 'sin texto todavía'}`}
        style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 6, boxShadow: '0 1px 6px rgba(30,42,44,0.18)', background: '#fff' }} />
      <figcaption style={{ fontSize: 11, color: 'var(--globant-muted)', marginTop: 4 }}>{fmt.label} · {fmt.width} × {fmt.height} px</figcaption>
    </figure>
  );
}

/** What the overlay colors mean (shown next to the preview when it is on). */
export function OverlayLegend({ format }) {
  const fmt = BRAND_FORMATS[format];
  const sw = (bg, border, dashed = false) => <span aria-hidden="true" style={{ display: 'inline-block', width: 14, height: 10, background: bg, border: `2px ${dashed ? 'dashed' : 'solid'} ${border}`, marginRight: 6, verticalAlign: 'middle' }} />;
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0', fontSize: 12, display: 'grid', gap: 3 }}>
      {fmt.cover.length > 0 && <li>{sw('rgba(180,54,44,0.28)', 'rgba(180,54,44,0.9)')}Lo que tapa o recorta el canal: {[...new Set(fmt.cover.map(c => c.label))].join('; ')}</li>}
      <li>{sw('transparent', 'rgba(45,95,153,0.95)', true)}Margen seguro para textos</li>
      <li>{sw('transparent', 'rgba(30,123,79,0.95)', true)}Zona recomendada para rostros dentro de cada foto</li>
      <li style={{ color: 'var(--globant-muted)' }}>{fmt.note} La guía no se exporta.</li>
    </ul>
  );
}
